import {
  UniverseOption,
  usePatternUniverses,
} from "@/contexts/PatternUniverse";
import { useToastHelper } from "@/helpers/helperFunctions";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { deployScannerRequest } from "../api/scanner";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { ScrollArea } from "../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export const DeployScannerModal = ({
  open,
  setOpen,
  scannerName,
  scannerId,
  userInfo,
  selectedUniverse,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  scannerName: string;
  scannerId: string;
  userInfo: any;
  selectedUniverse?: UniverseOption;
}) => {
  const [universeId, setUniverseId] = useState<string>(
    selectedUniverse?.value ?? ""
  );
  const { refetchUniverse, userUniverses } = usePatternUniverses();
  const [isDeploying, setIsDeploying] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToastHelper();
  const router = useRouter();

  // Ensure universeId is updated when selectedUniverse changes

  useEffect(() => {
    if (selectedUniverse) {
      setUniverseId(selectedUniverse.value);
    }
  }, [selectedUniverse]);

  const handleDeploy = useCallback(() => {
    if (!universeId) {
      setError("Please select a group"); // Set error state
      return;
    }
    setError(null); // Clear error state if universe is selected
    setIsDeploying(true);
    deployScannerRequest(scannerId, universeId, userInfo)
      .then((res) => {
        if (res.status === 200) {
          setIsDeploying(false);
          toast.success(res.data.message);

          // Close the modal first
          setOpen(false);

          // Navigate after the modal is closed
          router.push({
            pathname: "/deploy",
            query: { source: "deploy" },
          });
        } else {
          toast.error(res.response.data.detail ?? "Something went wrong");
          setIsDeploying(false);
        }
      })
      .catch((err) => {
        toast.error(err.response.data.detail ?? "Something went wrong");
        setIsDeploying(false);
      });
  }, [scannerId, universeId, userInfo, setOpen, router, toast]);

  // Fetch universes if not already loaded
  useEffect(() => {
    if (userUniverses.length === 0) {
      refetchUniverse(true, userInfo?.user_id);
    }
  }, [refetchUniverse, userUniverses]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deploy Scanner</DialogTitle>
          <DialogDescription>Select group.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="w-full flex flex-row gap-2">
            <Label className="text-muted-foreground font-bold">Scanner</Label>
            <Label className="font-bold">{scannerName}</Label>
          </div>
          <div className="w-full flex flex-row items-center gap-2">
            <Label className="text-muted-foreground font-bold">Group</Label>
            <Select value={universeId} onValueChange={setUniverseId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={selectedUniverse?.label ?? "Select group"}
                  defaultValue={selectedUniverse?.value}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <ScrollArea className="h-48 max-h-56">
                    {userUniverses.map((universe) => (
                      <SelectItem key={universe.value} value={universe.value}>
                        {universe.label}
                      </SelectItem>
                    ))}
                  </ScrollArea>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant={"secondary"} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={isDeploying}
            onClick={handleDeploy}
            className="bg-[--marketplace-blue-new]"
          >
            <>
              Deploy Now
              {isDeploying && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
            </>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
